import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, CreditCard, ArrowRight, ArrowLeft, Package, Truck, Save, Check, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PayPal icon component
const PayPalIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .76-.654h6.61c2.187 0 3.79.53 4.762 1.574.913.98 1.2 2.394.855 4.207-.46 2.42-1.524 4.147-3.164 5.135-1.557.94-3.59 1.417-6.043 1.417H6.84a.641.641 0 0 0-.632.54l-.955 4.79a.642.642 0 0 1-.633.54h-.544v.668z"/>
    <path d="M19.815 8.28c-.458 2.418-1.523 4.146-3.163 5.134-1.558.94-3.591 1.418-6.044 1.418H8.724a.641.641 0 0 0-.632.54l-1.2 6.01a.515.515 0 0 0 .508.598h3.567a.641.641 0 0 0 .632-.54l.694-3.474a.641.641 0 0 1 .632-.54h1.406c2.453 0 4.485-.478 6.044-1.418 1.64-.988 2.705-2.716 3.163-5.134.346-1.813.058-3.227-.855-4.207-.388-.417-.862-.76-1.417-1.025.183.858.188 1.79.016 2.789l-.467-.15z" opacity=".7"/>
  </svg>
);

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { cart, cartTotal } = useCart();
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [step, setStep] = useState(1); // 1: Address, 2: Shipping, 3: Review & Pay
  const [paymentMethod, setPaymentMethod] = useState("stripe"); // "stripe" or "paypal"
  
  // Saved addresses
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [saveAddress, setSaveAddress] = useState(true);
  const [loadingAddresses, setLoadingAddresses] = useState(true);

  // Shipping
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("GB");
  const [shippingQuote, setShippingQuote] = useState(null);
  const [loadingShipping, setLoadingShipping] = useState(false);
  
  const [addressData, setAddressData] = useState({
    fullName: user?.name || "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    county: "",
    postcode: "",
    countryCode: "GB",
    phone: "",
  });

  const [errors, setErrors] = useState({});

  // Promo code state (one-time first-order discount from newsletter signup)
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // { code, discount_percent }
  const [promoError, setPromoError] = useState("");
  const [validatingPromo, setValidatingPromo] = useState(false);

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setValidatingPromo(true);
    setPromoError("");
    try {
      const { data } = await axios.post(`${API}/newsletter/validate`, { code });
      setPromoApplied({ code: data.code, discount_percent: data.discount_percent });
      toast.success(`${data.discount_percent}% discount applied!`);
    } catch (err) {
      setPromoApplied(null);
      setPromoError(err.response?.data?.detail || "Couldn't validate code");
    } finally {
      setValidatingPromo(false);
    }
  };

  const removePromo = () => {
    setPromoApplied(null);
    setPromoInput("");
    setPromoError("");
  };

  // Fetch saved addresses and shipping countries on mount
  useEffect(() => {
    if (!user) {
      navigate("/login", { state: { from: "/checkout" } });
      return;
    }
    if (cart.length === 0) {
      navigate("/cart");
      return;
    }
    
    fetchSavedAddresses();
    fetchShippingCountries();
  }, [user, cart, navigate]);

  const fetchSavedAddresses = async () => {
    try {
      const res = await axios.get(`${API}/addresses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedAddresses(res.data);
      
      // Auto-select default address if exists
      const defaultAddr = res.data.find(a => a.is_default) || res.data[0];
      if (defaultAddr) {
        setSelectedAddressId(defaultAddr.id);
        populateAddressForm(defaultAddr);
      } else {
        setShowNewAddressForm(true);
      }
    } catch (error) {
      console.error("Failed to fetch addresses:", error);
      setShowNewAddressForm(true);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const fetchShippingCountries = async () => {
    try {
      const res = await axios.get(`${API}/shipping/countries`);
      setCountries(res.data);
    } catch (error) {
      console.error("Failed to fetch countries:", error);
    }
  };

  const populateAddressForm = (address) => {
    setAddressData({
      fullName: address.full_name,
      addressLine1: address.address_line1,
      addressLine2: address.address_line2 || "",
      city: address.city,
      county: address.county || "",
      postcode: address.postcode,
      countryCode: address.country_code || "GB",
      phone: address.phone,
    });
    setSelectedCountry(address.country_code || "GB");
  };

  const handleAddressSelect = (addressId) => {
    setSelectedAddressId(addressId);
    const address = savedAddresses.find(a => a.id === addressId);
    if (address) {
      populateAddressForm(address);
      setShowNewAddressForm(false);
    }
  };

  const validateAddress = () => {
    const newErrors = {};
    
    if (!addressData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }
    if (!addressData.addressLine1.trim()) {
      newErrors.addressLine1 = "Address is required";
    }
    if (!addressData.city.trim()) {
      newErrors.city = "City is required";
    }
    if (!addressData.postcode.trim()) {
      newErrors.postcode = "Postcode is required";
    }
    if (!addressData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[\d\s+()-]{10,}$/.test(addressData.phone.trim())) {
      newErrors.phone = "Please enter a valid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinueToShipping = async () => {
    if (!validateAddress()) return;
    
    // Save address if requested
    if (saveAddress && showNewAddressForm) {
      try {
        await axios.post(`${API}/addresses`, {
          full_name: addressData.fullName,
          address_line1: addressData.addressLine1,
          address_line2: addressData.addressLine2,
          city: addressData.city,
          county: addressData.county,
          postcode: addressData.postcode,
          country_code: addressData.countryCode,
          phone: addressData.phone,
          is_default: savedAddresses.length === 0
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Address saved for future orders");
      } catch (error) {
        console.error("Failed to save address:", error);
      }
    }
    
    // Calculate shipping
    await calculateShipping();
    setStep(2);
  };

  const calculateShipping = async () => {
    setLoadingShipping(true);
    try {
      // Get total dimensions from cart
      const totalVolume = cart.reduce((sum, item) => {
        const w = item.config.width || 4;
        const h = item.config.height || 3;
        const d = item.config.depth || 2;
        return sum + (w * h * d * item.quantity);
      }, 0);
      
      // Use first item's dimensions for quote (simplified)
      const firstItem = cart[0];
      const res = await axios.post(`${API}/shipping/calculate`, {
        country_code: addressData.countryCode,
        width: firstItem.config.width || 4,
        height: firstItem.config.height || 3,
        depth: firstItem.config.depth || 2,
        quantity: cart.reduce((sum, item) => sum + item.quantity, 0)
      });
      setShippingQuote(res.data);
    } catch (error) {
      console.error("Failed to calculate shipping:", error);
      toast.error("Failed to calculate shipping");
    } finally {
      setLoadingShipping(false);
    }
  };

  const formatAddress = () => {
    const countryName = countries.find(c => c.code === addressData.countryCode)?.name || addressData.countryCode;
    const parts = [
      addressData.fullName,
      addressData.addressLine1,
      addressData.addressLine2,
      addressData.city,
      addressData.county,
      addressData.postcode,
      countryName,
      `Tel: ${addressData.phone}`
    ].filter(Boolean);
    return parts.join(", ");
  };

  const handleCheckout = async () => {
    setProcessingCheckout(true);
    
    try {
      // Create order
      const orderResponse = await axios.post(
        `${API}/orders/create`,
        {
          items: cart.map(item => ({
            config: item.config,
            quantity: item.quantity
          })),
          shipping_address: formatAddress(),
          payment_method: paymentMethod,
          promo_code: promoApplied?.code || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (paymentMethod === "stripe") {
        // Stripe checkout
        const checkoutResponse = await axios.post(
          `${API}/payments/create-checkout`,
          {
            order_id: orderResponse.data.order_id,
            origin_url: window.location.origin
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.location.href = checkoutResponse.data.checkout_url;
      } else if (paymentMethod === "paypal") {
        // PayPal checkout
        const paypalResponse = await axios.post(
          `${API}/payments/paypal/create`,
          {
            order_id: orderResponse.data.order_id,
            origin_url: window.location.origin
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.location.href = paypalResponse.data.approval_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Checkout failed. Please try again.");
      setProcessingCheckout(false);
    }
  };

  const discountAmount = promoApplied
    ? Math.round(cartTotal * (promoApplied.discount_percent / 100) * 100) / 100
    : 0;
  const grandTotal = cartTotal - discountAmount + (shippingQuote?.total || 0);

  if (!user || cart.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen pt-24 px-4 bg-[#0A0A0A]">
      <div className="max-w-2xl mx-auto">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              step >= 1 ? 'bg-[#E07A5F] text-white' : 'bg-white/10 text-white/40'
            }`}>
              <MapPin className="w-5 h-5" />
            </div>
            <span className={`ml-2 mr-4 text-sm ${step >= 1 ? 'text-[#F4F1DE]' : 'text-white/40'}`}>
              Address
            </span>
          </div>
          <div className="w-8 h-0.5 bg-white/10" />
          <div className="flex items-center mx-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              step >= 2 ? 'bg-[#E07A5F] text-white' : 'bg-white/10 text-white/40'
            }`}>
              <Truck className="w-5 h-5" />
            </div>
            <span className={`ml-2 mr-4 text-sm ${step >= 2 ? 'text-[#F4F1DE]' : 'text-white/40'}`}>
              Shipping
            </span>
          </div>
          <div className="w-8 h-0.5 bg-white/10" />
          <div className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              step >= 3 ? 'bg-[#E07A5F] text-white' : 'bg-white/10 text-white/40'
            }`}>
              <CreditCard className="w-5 h-5" />
            </div>
            <span className={`ml-2 text-sm ${step >= 3 ? 'text-[#F4F1DE]' : 'text-white/40'}`}>
              Payment
            </span>
          </div>
        </div>

        {/* Step 1: Delivery Address */}
        {step === 1 && (
          <div className="bg-[#121212] border border-white/10 p-6 md:p-8" data-testid="checkout-address-form">
            <h2 className="text-2xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-6">
              Delivery Address
            </h2>
            
            {/* Saved Addresses */}
            {!loadingAddresses && savedAddresses.length > 0 && !showNewAddressForm && (
              <div className="mb-6">
                <Label className="text-[#F4F1DE]/80 mb-3 block">Select a saved address</Label>
                <div className="space-y-3">
                  {savedAddresses.map((addr) => (
                    <div
                      key={addr.id}
                      onClick={() => handleAddressSelect(addr.id)}
                      className={`p-4 border cursor-pointer transition-colors ${
                        selectedAddressId === addr.id
                          ? 'border-[#E07A5F] bg-[#E07A5F]/10'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                      data-testid={`saved-address-${addr.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-[#F4F1DE]">{addr.full_name}</p>
                          <p className="text-[#F4F1DE]/60 text-sm">
                            {addr.address_line1}, {addr.city}, {addr.postcode}
                          </p>
                          <p className="text-[#F4F1DE]/40 text-sm">
                            {countries.find(c => c.code === addr.country_code)?.name || addr.country_code}
                          </p>
                        </div>
                        {selectedAddressId === addr.id && (
                          <Check className="w-5 h-5 text-[#E07A5F]" />
                        )}
                      </div>
                      {addr.is_default && (
                        <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-[#E07A5F]/20 text-[#E07A5F] rounded">
                          Default
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowNewAddressForm(true);
                    setSelectedAddressId(null);
                    setAddressData({
                      fullName: user?.name || "",
                      addressLine1: "",
                      addressLine2: "",
                      city: "",
                      county: "",
                      postcode: "",
                      countryCode: "GB",
                      phone: "",
                    });
                  }}
                  className="mt-4 text-[#E07A5F] hover:text-[#D06A4F]"
                  data-testid="add-new-address-btn"
                >
                  + Add new address
                </Button>
              </div>
            )}

            {/* New Address Form */}
            {(showNewAddressForm || savedAddresses.length === 0) && (
              <div className="space-y-4">
                {savedAddresses.length > 0 && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowNewAddressForm(false);
                      if (savedAddresses.length > 0) {
                        handleAddressSelect(savedAddresses[0].id);
                      }
                    }}
                    className="text-[#F4F1DE]/60 hover:text-[#F4F1DE] mb-2"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to saved addresses
                  </Button>
                )}

                <div>
                  <Label htmlFor="fullName" className="text-[#F4F1DE]/80">Full Name *</Label>
                  <Input
                    id="fullName"
                    value={addressData.fullName}
                    onChange={(e) => setAddressData(prev => ({ ...prev, fullName: e.target.value }))}
                    className={`mt-2 bg-[#0A0A0A] border-white/10 text-[#F4F1DE] h-12 ${errors.fullName ? 'border-red-500' : ''}`}
                    placeholder="John Smith"
                    data-testid="checkout-fullname"
                  />
                  {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
                </div>

                <div>
                  <Label htmlFor="countryCode" className="text-[#F4F1DE]/80">Country *</Label>
                  <div className="relative mt-2">
                    <select
                      id="countryCode"
                      value={addressData.countryCode}
                      onChange={(e) => setAddressData(prev => ({ ...prev, countryCode: e.target.value }))}
                      className="w-full h-12 bg-[#0A0A0A] border border-white/10 text-[#F4F1DE] px-4 appearance-none cursor-pointer rounded-md"
                      data-testid="checkout-country"
                    >
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#F4F1DE]/40 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="addressLine1" className="text-[#F4F1DE]/80">Address Line 1 *</Label>
                  <Input
                    id="addressLine1"
                    value={addressData.addressLine1}
                    onChange={(e) => setAddressData(prev => ({ ...prev, addressLine1: e.target.value }))}
                    className={`mt-2 bg-[#0A0A0A] border-white/10 text-[#F4F1DE] h-12 ${errors.addressLine1 ? 'border-red-500' : ''}`}
                    placeholder="123 Main Street"
                    data-testid="checkout-address1"
                  />
                  {errors.addressLine1 && <p className="text-red-500 text-sm mt-1">{errors.addressLine1}</p>}
                </div>

                <div>
                  <Label htmlFor="addressLine2" className="text-[#F4F1DE]/80">Address Line 2</Label>
                  <Input
                    id="addressLine2"
                    value={addressData.addressLine2}
                    onChange={(e) => setAddressData(prev => ({ ...prev, addressLine2: e.target.value }))}
                    className="mt-2 bg-[#0A0A0A] border-white/10 text-[#F4F1DE] h-12"
                    placeholder="Flat 2, Building B (optional)"
                    data-testid="checkout-address2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city" className="text-[#F4F1DE]/80">City *</Label>
                    <Input
                      id="city"
                      value={addressData.city}
                      onChange={(e) => setAddressData(prev => ({ ...prev, city: e.target.value }))}
                      className={`mt-2 bg-[#0A0A0A] border-white/10 text-[#F4F1DE] h-12 ${errors.city ? 'border-red-500' : ''}`}
                      placeholder="London"
                      data-testid="checkout-city"
                    />
                    {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                  </div>

                  <div>
                    <Label htmlFor="county" className="text-[#F4F1DE]/80">County / Region</Label>
                    <Input
                      id="county"
                      value={addressData.county}
                      onChange={(e) => setAddressData(prev => ({ ...prev, county: e.target.value }))}
                      className="mt-2 bg-[#0A0A0A] border-white/10 text-[#F4F1DE] h-12"
                      placeholder="Greater London"
                      data-testid="checkout-county"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postcode" className="text-[#F4F1DE]/80">Postcode / ZIP *</Label>
                    <Input
                      id="postcode"
                      value={addressData.postcode}
                      onChange={(e) => setAddressData(prev => ({ ...prev, postcode: e.target.value.toUpperCase() }))}
                      className={`mt-2 bg-[#0A0A0A] border-white/10 text-[#F4F1DE] h-12 ${errors.postcode ? 'border-red-500' : ''}`}
                      placeholder="SW1A 1AA"
                      data-testid="checkout-postcode"
                    />
                    {errors.postcode && <p className="text-red-500 text-sm mt-1">{errors.postcode}</p>}
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-[#F4F1DE]/80">Phone Number *</Label>
                    <Input
                      id="phone"
                      value={addressData.phone}
                      onChange={(e) => setAddressData(prev => ({ ...prev, phone: e.target.value }))}
                      className={`mt-2 bg-[#0A0A0A] border-white/10 text-[#F4F1DE] h-12 ${errors.phone ? 'border-red-500' : ''}`}
                      placeholder="+44 7123 456789"
                      data-testid="checkout-phone"
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>
                </div>

                {/* Save address checkbox */}
                <label className="flex items-center gap-3 cursor-pointer mt-4">
                  <input
                    type="checkbox"
                    checked={saveAddress}
                    onChange={(e) => setSaveAddress(e.target.checked)}
                    className="w-5 h-5 rounded border-white/20 bg-[#0A0A0A] text-[#E07A5F] focus:ring-[#E07A5F]"
                  />
                  <span className="text-[#F4F1DE]/80 flex items-center">
                    <Save className="w-4 h-4 mr-2" />
                    Save this address for future orders
                  </span>
                </label>
              </div>
            )}

            <div className="flex gap-4 mt-8">
              <Button
                variant="outline"
                onClick={() => navigate("/cart")}
                className="flex-1 border-white/20 text-[#F4F1DE]/80 hover:border-white/40"
                data-testid="checkout-back"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Cart
              </Button>
              <Button
                onClick={handleContinueToShipping}
                className="flex-1 bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
                data-testid="checkout-continue"
              >
                Continue to Shipping
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Shipping */}
        {step === 2 && (
          <div className="bg-[#121212] border border-white/10 p-6 md:p-8" data-testid="checkout-shipping">
            <h2 className="text-2xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-6">
              Shipping Options
            </h2>

            {loadingShipping ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner" />
              </div>
            ) : shippingQuote?.available ? (
              <div className="space-y-4">
                {/* Shipping Option */}
                <div className="border border-[#E07A5F] bg-[#E07A5F]/10 p-4 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-3">
                      <Truck className="w-6 h-6 text-[#E07A5F] mt-1" />
                      <div>
                        <p className="font-bold text-[#F4F1DE]">Standard Delivery</p>
                        <p className="text-[#F4F1DE]/60 text-sm">
                          {shippingQuote.zone}
                        </p>
                        <p className="text-[#F4F1DE]/60 text-sm mt-1">
                          Estimated {shippingQuote.delivery_days.min}-{shippingQuote.delivery_days.max} working days
                        </p>
                        {shippingQuote.note && (
                          <p className="text-yellow-500/80 text-sm mt-2">{shippingQuote.note}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-[#E07A5F]">£{shippingQuote.total.toFixed(2)}</p>
                      <p className="text-[#F4F1DE]/40 text-xs">inc. handling</p>
                    </div>
                  </div>
                </div>

                {/* Shipping Info */}
                <div className="bg-[#0A0A0A] border border-white/5 p-4 rounded-lg">
                  <p className="text-[#F4F1DE]/60 text-sm">
                    <span className="font-medium text-[#F4F1DE]">Delivery to:</span> {shippingQuote.country}
                  </p>
                  {shippingQuote.estimated_weight_kg && (
                    <p className="text-[#F4F1DE]/40 text-sm mt-1">
                      Estimated weight: {shippingQuote.estimated_weight_kg}kg
                    </p>
                  )}
                  <p className="text-[#F4F1DE]/40 text-sm mt-2">
                    All enclosures are professionally packaged and shipped via trusted courier services.
                    Tracking information will be provided once dispatched.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-red-400">{shippingQuote?.reason || "Shipping not available to this location"}</p>
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="mt-4 border-white/20 text-[#F4F1DE]/80"
                >
                  Change Address
                </Button>
              </div>
            )}

            <div className="flex gap-4 mt-8">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="border-white/20 text-[#F4F1DE]/80 hover:border-white/40"
                data-testid="checkout-back-step2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!shippingQuote?.available}
                className="flex-1 bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
                data-testid="checkout-continue-step2"
              >
                Continue to Payment
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Review & Pay */}
        {step === 3 && (
          <div className="space-y-6" data-testid="checkout-review">
            {/* Delivery Address Summary */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-bold font-['Space_Grotesk'] text-[#F4F1DE]">
                  <MapPin className="w-5 h-5 inline mr-2 text-[#E07A5F]" />
                  Delivery Address
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep(1)}
                  className="text-[#E07A5F] hover:text-[#D06A4F]"
                  data-testid="checkout-edit-address"
                >
                  Edit
                </Button>
              </div>
              <div className="text-[#F4F1DE]/80 space-y-1">
                <p className="font-medium">{addressData.fullName}</p>
                <p>{addressData.addressLine1}</p>
                {addressData.addressLine2 && <p>{addressData.addressLine2}</p>}
                <p>{addressData.city}{addressData.county && `, ${addressData.county}`}</p>
                <p>{addressData.postcode}</p>
                <p className="text-[#F4F1DE]/60">
                  {countries.find(c => c.code === addressData.countryCode)?.name}
                </p>
                <p className="text-[#F4F1DE]/60">{addressData.phone}</p>
              </div>
            </div>

            {/* Shipping Summary */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-bold font-['Space_Grotesk'] text-[#F4F1DE]">
                  <Truck className="w-5 h-5 inline mr-2 text-[#E07A5F]" />
                  Shipping
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep(2)}
                  className="text-[#E07A5F] hover:text-[#D06A4F]"
                >
                  Edit
                </Button>
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-[#F4F1DE]/80">Standard Delivery</p>
                  <p className="text-[#F4F1DE]/60 text-sm">
                    {shippingQuote?.delivery_days.min}-{shippingQuote?.delivery_days.max} working days
                  </p>
                </div>
                <p className="text-[#E07A5F] font-bold">£{shippingQuote?.total.toFixed(2)}</p>
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <h3 className="text-lg font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-4">
                <Package className="w-5 h-5 inline mr-2 text-[#E07A5F]" />
                Order Summary
              </h3>
              
              <div className="space-y-3 mb-4">
                {cart.map((item) => (
                  <div key={item.id} className="flex justify-between text-[#F4F1DE]/80">
                    <span>
                      {item.type === "stacking" 
                        ? `Stacking ${item.config.size_name} × ${item.config.stack_count}`
                        : `${item.config.width}×${item.config.height}×${item.config.depth}ft ${item.config.shape}`
                      }
                      {item.quantity > 1 && ` (×${item.quantity})`}
                    </span>
                    <span>£{item.price.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              
              <div className="border-t border-white/10 pt-4 space-y-2">
                <div className="flex justify-between text-[#F4F1DE]/60">
                  <span>Subtotal</span>
                  <span>£{cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#F4F1DE]/60">
                  <span>Shipping</span>
                  <span>£{shippingQuote?.total.toFixed(2)}</span>
                </div>

                {/* Promo code */}
                {!promoApplied ? (
                  <div className="pt-3 space-y-2" data-testid="promo-code-section">
                    <label className="text-xs uppercase tracking-wider text-[#F4F1DE]/50">
                      Promo code
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                        placeholder="REPTI-XXXXXX"
                        className="flex-1 bg-[#0A0A0A] border border-white/10 rounded px-3 py-2 text-sm font-mono text-[#F4F1DE] placeholder-[#F4F1DE]/25 focus:border-[#E07A5F] focus:outline-none"
                        data-testid="promo-code-input"
                      />
                      <button
                        onClick={applyPromo}
                        disabled={validatingPromo || !promoInput.trim()}
                        className="px-4 py-2 text-sm bg-[#E07A5F]/20 hover:bg-[#E07A5F]/30 border border-[#E07A5F]/40 text-[#E07A5F] font-semibold rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="promo-apply-btn"
                      >
                        {validatingPromo ? "..." : "Apply"}
                      </button>
                    </div>
                    {promoError && (
                      <p className="text-xs text-red-400" data-testid="promo-error">{promoError}</p>
                    )}
                  </div>
                ) : (
                  <div
                    className="flex justify-between items-center text-green-400 pt-3"
                    data-testid="promo-applied"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs bg-green-500/10 border border-green-500/30 px-2 py-0.5 rounded">
                        {promoApplied.code}
                      </span>
                      <button
                        onClick={removePromo}
                        className="text-xs text-[#F4F1DE]/40 hover:text-[#F4F1DE]/70"
                        data-testid="promo-remove-btn"
                      >
                        remove
                      </button>
                    </div>
                    <span>-£{discountAmount.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between text-xl font-bold text-[#F4F1DE] pt-2 border-t border-white/10">
                  <span>Total</span>
                  <span className="text-[#E07A5F]">£{grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <h3 className="text-lg font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-4">
                <CreditCard className="w-5 h-5 inline mr-2 text-[#E07A5F]" />
                Payment Method
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Stripe Option */}
                <button
                  onClick={() => setPaymentMethod("stripe")}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    paymentMethod === "stripe"
                      ? "border-[#E07A5F] bg-[#E07A5F]/10"
                      : "border-white/10 hover:border-white/30"
                  }`}
                  data-testid="payment-stripe"
                >
                  <div className="flex flex-col items-center gap-2">
                    <CreditCard className={`w-8 h-8 ${paymentMethod === "stripe" ? "text-[#E07A5F]" : "text-[#F4F1DE]/60"}`} />
                    <span className={`font-medium ${paymentMethod === "stripe" ? "text-[#F4F1DE]" : "text-[#F4F1DE]/60"}`}>
                      Card
                    </span>
                    <span className="text-xs text-[#F4F1DE]/40">Visa, Mastercard, Amex</span>
                  </div>
                </button>

                {/* PayPal Option */}
                <button
                  onClick={() => setPaymentMethod("paypal")}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    paymentMethod === "paypal"
                      ? "border-[#0070ba] bg-[#0070ba]/10"
                      : "border-white/10 hover:border-white/30"
                  }`}
                  data-testid="payment-paypal"
                >
                  <div className="flex flex-col items-center gap-2">
                    <PayPalIcon />
                    <span className={`font-medium ${paymentMethod === "paypal" ? "text-[#F4F1DE]" : "text-[#F4F1DE]/60"}`}>
                      PayPal
                    </span>
                    <span className="text-xs text-[#F4F1DE]/40">Pay with PayPal</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Payment Button */}
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                className="border-white/20 text-[#F4F1DE]/80 hover:border-white/40"
                data-testid="checkout-back-step3"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleCheckout}
                disabled={processingCheckout}
                className={`flex-1 py-6 font-bold uppercase tracking-widest btn-glow ${
                  paymentMethod === "paypal" 
                    ? "bg-[#0070ba] hover:bg-[#005ea6] text-white"
                    : "bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
                }`}
                data-testid="checkout-pay"
              >
                {processingCheckout ? (
                  <span className="spinner" />
                ) : paymentMethod === "paypal" ? (
                  <>
                    <PayPalIcon />
                    <span className="ml-2">Pay with PayPal</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay £{grandTotal.toFixed(2)}
                  </>
                )}
              </Button>
            </div>
            
            <p className="text-xs text-[#F4F1DE]/40 text-center">
              {paymentMethod === "stripe" 
                ? "Secure checkout powered by Stripe. Your payment details are never stored on our servers."
                : "You will be redirected to PayPal to complete your payment securely."
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutPage;
